import { skipToken } from "@reduxjs/toolkit/query";
import type { PlaybookTestToolMock } from "@repo/app-types/entities/playbookTestCase";
import {
  getCustomToolMock,
  ToolType,
  type Tool,
} from "@repo/app-types/entities/tool";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Switch } from "@repo/ui/Switch/switch";
import { IconChevronDown, IconTool } from "@tabler/icons-react";
import { useMemo, type FC } from "react";
import {
  Button as AriaButton,
  Disclosure,
  DisclosurePanel,
  Heading,
} from "react-aria-components";
import { useParams } from "react-router";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { useAllToolsById } from "../../hooks/useAllToolsById.js";

/** A response of nothing is no override, so the tool keeps its own mock. */
const checkToolMockEmpty = (toolMock: PlaybookTestToolMock): boolean =>
  toolMock.response.trim() === "";

/**
 * The mocks as they leave the editor: a row nobody typed a response into is
 * nothing yet, not an override the case has to carry. The editor keeps its own
 * empty rows so they stay editable.
 */
export const dropIncompletePlaybookTestToolMocks = (
  toolMocks: readonly PlaybookTestToolMock[] | null,
): PlaybookTestToolMock[] | null => {
  if (!toolMocks) {
    return null;
  }

  return toolMocks.filter((toolMock) => !checkToolMockEmpty(toolMock));
};

/**
 * What the mocks cover, in one line, so a collapsed section says how much of
 * the agent this case overrides: "2 of 4 tools".
 */
export const describePlaybookTestToolMocks = (
  toolMocks: readonly PlaybookTestToolMock[] | null,
  tools: readonly Tool[],
): string => {
  if (tools.length === 0) {
    return "No custom tools";
  }

  const overriddenCount = (toolMocks ?? []).filter(
    (toolMock) =>
      !checkToolMockEmpty(toolMock) &&
      tools.some((tool) => tool.id === toolMock.tool_id),
  ).length;

  if (overriddenCount === 0) {
    return "Not set";
  }

  return `${overriddenCount} of ${tools.length} ${
    tools.length === 1 ? "tool" : "tools"
  }`;
};

/**
 * The custom and code tools the agent under test is configured with, in
 * the agent's own order. Only their responses can be pinned, so the others are left
 * out rather than offered as fields the run would ignore. Both queries are
 * already loaded by the surfaces around this one, so the editor reads them from
 * the cache rather than fetching anything of its own.
 */
const useAttachedAgentCustomTools = (): readonly Tool[] => {
  const { teammateAgentId } = useParams();

  const { data: teammateAgent } = useGetTeammateAgentQuery(
    teammateAgentId ?? skipToken,
  );
  const toolsById = useAllToolsById();

  return useMemo(
    () =>
      (teammateAgent?.teammate_agent_config.tool_ids ?? [])
        .map((toolId) => toolsById.get(toolId))
        .filter((tool): tool is Tool => tool !== undefined)
        .filter(
          (tool) =>
            tool.type === ToolType.CUSTOM || tool.type === ToolType.CODE,
        ),
    [teammateAgent, toolsById],
  );
};

interface PlaybookTestToolMockRowProps {
  tool: Tool;
  toolMock: PlaybookTestToolMock | undefined;
  onChange: (toolMock: PlaybookTestToolMock) => void;
}

/** One tool's override: what it returns, and whether that is an error. */
const PlaybookTestToolMockRow: FC<PlaybookTestToolMockRowProps> = (props) => {
  const { tool, toolMock, onChange } = props;
  const defaultResponseDescription = getCustomToolMock(tool)
    ? "This tool uses its own mock response."
    : "This tool has no mock, so a response is written for the conversation.";

  const response = toolMock?.response ?? "";
  const hasResponse = response.trim() !== "";

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        label={tool.name}
        value={response}
        onChange={(newResponse) =>
          onChange({
            tool_id: tool.id,
            response: newResponse,
            is_error: toolMock?.is_error,
          })
        }
        placeholder="What this tool returns in this test case."
        description={
          hasResponse
            ? "Returned to the agent as written, without a model rewrite."
            : defaultResponseDescription
        }
      />
      <Switch
        label="Return as an error"
        isSelected={toolMock?.is_error ?? false}
        isDisabled={!hasResponse}
        onChange={(isError) =>
          onChange({
            tool_id: tool.id,
            response,
            // Left off rather than stored as false, so an untouched toggle
            // reads as the default it is.
            is_error: isError ? true : undefined,
          })
        }
      />
    </div>
  );
};

export interface PlaybookTestToolMocksEditorProps {
  tools: readonly Tool[];
  value: readonly PlaybookTestToolMock[];
  onChange: (value: PlaybookTestToolMock[]) => void;
}

/**
 * What each of the agent's custom tools returns in this test case. A tool the
 * case says nothing about behaves exactly as it does outside the suite.
 */
export const PlaybookTestToolMocksEditor: FC<
  PlaybookTestToolMocksEditorProps
> = (props) => {
  const { tools, value, onChange } = props;

  if (tools.length === 0) {
    return (
      <FieldDescription>
        This agent has no custom tools, so there is nothing to pin.
      </FieldDescription>
    );
  }

  // A mock for a tool the agent no longer has is left alone: reattaching the
  // tool should bring its override back rather than find it erased.
  const setToolMock = (toolMock: PlaybookTestToolMock) => {
    const isKnown = value.some(
      (existing) => existing.tool_id === toolMock.tool_id,
    );

    onChange(
      isKnown
        ? value.map((existing) =>
            existing.tool_id === toolMock.tool_id ? toolMock : existing,
          )
        : [...value, toolMock],
    );
  };

  return (
    <div className="space-y-4">
      <FieldDescription>
        These are the agent&rsquo;s custom tools. Every other tool behaves as it
        does outside the suite.
      </FieldDescription>
      {tools.map((tool) => (
        <PlaybookTestToolMockRow
          key={tool.id}
          tool={tool}
          toolMock={value.find((toolMock) => toolMock.tool_id === tool.id)}
          onChange={setToolMock}
        />
      ))}
    </div>
  );
};

export interface PlaybookTestToolMocksSectionProps {
  value: readonly PlaybookTestToolMock[] | null;
  onChange: (value: PlaybookTestToolMock[]) => void;
}

/**
 * The editor behind a collapsed disclosure that says how many of the agent's
 * custom tools this case overrides, so the count is readable without opening
 * it.
 */
export const PlaybookTestToolMocksSection: FC<
  PlaybookTestToolMocksSectionProps
> = (props) => {
  const { value, onChange } = props;

  const tools = useAttachedAgentCustomTools();

  return (
    <Disclosure className="rounded-lg border border-border-light bg-white">
      <Heading>
        <AriaButton
          slot="trigger"
          className="group w-full min-w-0 cursor-pointer text-left"
        >
          <div className="flex items-center gap-3 px-3 py-2.5">
            <IconTool className="size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1 text-sm font-medium text-foreground">
              Tool mocks
            </div>
            <div className="min-w-0 truncate text-sm text-muted-foreground">
              {describePlaybookTestToolMocks(value, tools)}
            </div>
            <IconChevronDown className="size-4 shrink-0 text-gray-400 transition-transform group-aria-expanded:rotate-180" />
          </div>
        </AriaButton>
      </Heading>
      <DisclosurePanel>
        <div className="border-t border-border-light px-3 py-3">
          <PlaybookTestToolMocksEditor
            tools={tools}
            value={value ?? []}
            onChange={onChange}
          />
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};
